import React from "react";
import { CheckCircle, XCircle, AlertTriangle, Info } from "lucide-react";
import DuplicateResolver from "./DuplicateResolver";
import ErrorsTable from "./ErrorsTable";

const Stat = ({ icon, label, value, color, theme }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "4px",
      padding: "14px 20px",
      borderRadius: "10px",
      background:
        theme === "dark" ? `${color}18` : `${color}11`,
      border: `1px solid ${color}33`,
      flex: "1 1 100px",
      minWidth: "90px",
    }}
  >
    <div style={{ color }}>{icon}</div>
    <div
      style={{
        fontSize: "22px",
        fontWeight: "800",
        color,
        lineHeight: 1,
      }}
    >
      {value ?? 0}
    </div>
    <div
      style={{
        fontSize: "11px",
        fontWeight: "600",
        color: theme === "dark" ? "rgba(255,255,255,0.55)" : "#6b7280",
        textAlign: "center",
      }}
    >
      {label}
    </div>
  </div>
);

const ResultsTab = ({
  theme,
  language,
  importResult,
  duplicateSelections,
  onDuplicateSelect,
  onUpdateDuplicate,
  onResolveBulk,
  isResolvingDuplicates,
}) => {
  const isAr = language === "ar";

  if (!importResult) {
    return (
      <div
        style={{
          padding: "40px",
          textAlign: "center",
          color: theme === "dark" ? "rgba(255,255,255,0.4)" : "#9ca3af",
          fontSize: "14px",
        }}
      >
        {isAr ? "لا توجد نتائج بعد" : "No results yet"}
      </div>
    );
  }

  const {
    skipped = 0,
    errors = [],
    duplicates = [],
    processed = [],
    total,
  } = importResult;
  // Fall back to counting the processed rows when the counts weren't merged in.
  const inserted =
    importResult.inserted ?? processed.filter((p) => p.created).length;
  const updated =
    importResult.updated ?? processed.filter((p) => p.updated).length;

  const totalProcessed = total ?? inserted + updated + skipped + errors.length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      {/* Summary stats */}
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
        <Stat
          theme={theme}
          icon={<Info size={18} />}
          label={isAr ? "الإجمالي" : "Total"}
          value={totalProcessed}
          color="#3b82f6"
        />
        <Stat
          theme={theme}
          icon={<CheckCircle size={18} />}
          label={isAr ? "تم الإدراج" : "Inserted"}
          value={inserted}
          color="#10b981"
        />
        <Stat
          theme={theme}
          icon={<CheckCircle size={18} />}
          label={isAr ? "تم التحديث" : "Updated"}
          value={updated}
          color="#6366f1"
        />
        <Stat
          theme={theme}
          icon={<AlertTriangle size={18} />}
          label={isAr ? "تكرارات" : "Duplicates"}
          value={duplicates?.length ?? 0}
          color="#f59e0b"
        />
        <Stat
          theme={theme}
          icon={<XCircle size={18} />}
          label={isAr ? "أخطاء" : "Errors"}
          value={errors?.length ?? 0}
          color="#ef4444"
        />
      </div>

      {/* Duplicates */}
      {duplicates && duplicates.length > 0 && (
        <div>
          <h4
            style={{
              margin: "0 0 10px 0",
              fontSize: "13px",
              fontWeight: "700",
              color: theme === "dark" ? "#fbbf24" : "#92400e",
            }}
          >
            {isAr
              ? `تعارضات تحتاج مراجعة (${duplicates.length})`
              : `Duplicates Requiring Resolution (${duplicates.length})`}
          </h4>
          <DuplicateResolver
            theme={theme}
            language={language}
            duplicates={duplicates}
            duplicateSelections={duplicateSelections}
            onSelect={onDuplicateSelect}
            onUpdate={onUpdateDuplicate}
            onResolveBulk={onResolveBulk}
            isResolvingDuplicates={isResolvingDuplicates}
          />
        </div>
      )}

      {/* Errors */}
      {errors && errors.length > 0 && (
        <ErrorsTable theme={theme} language={language} errors={errors} />
      )}

      {/* All good */}
      {(!duplicates || duplicates.length === 0) &&
        (!errors || errors.length === 0) && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "14px 18px",
              borderRadius: "10px",
              background:
                theme === "dark"
                  ? "rgba(16,185,129,0.12)"
                  : "rgba(209,250,229,0.8)",
              border:
                theme === "dark"
                  ? "1px solid rgba(16,185,129,0.3)"
                  : "1px solid rgba(16,185,129,0.3)",
              color: theme === "dark" ? "#6ee7b7" : "#065f46",
              fontWeight: "600",
              fontSize: "13px",
            }}
          >
            <CheckCircle size={18} />
            {isAr
              ? "تم الاستيراد بنجاح دون أي مشاكل!"
              : "Import completed successfully with no issues!"}
          </div>
        )}
    </div>
  );
};

export default ResultsTab;